import { motion } from "framer-motion";
import { ShieldCheck, BadgeCheck, Wallet, Clock, UserCheck, Sparkles } from "lucide-react";

const FEATURES = [
  { icon: BadgeCheck, title: "Certified & Compassionate", desc: "Every caregiver is trained, background-checked, and matched with care recipients based on personality, needs, and language." },
  { icon: Sparkles, title: "Flexible Care Plans", desc: "Hourly, overnight, live-in, or 24/7. Plans adjust as health needs evolve, without lock-in contracts." },
  { icon: Wallet, title: "Affordable & Reliable", desc: "Transparent pricing, no surprise fees, and consistent caregiver assignments your family can count on." },
  { icon: Clock, title: "24/7 Availability", desc: "Round-the-clock scheduling support and emergency response, including evenings, weekends, and holidays." },
  { icon: UserCheck, title: "Personalised Home Support", desc: "Care plans built collaboratively with the family, primary care provider, and the client themselves." },
  { icon: ShieldCheck, title: "Safety-Focused Approach", desc: "Fall-prevention, infection-control, and PIPEDA-conscious privacy practices on every shift." },
];

export default function WhyChooseUs() {
  return (
    <section id="why-us" className="py-20 md:py-32 bg-[#1E5F8C] text-[#F8FAFC] relative overflow-hidden" data-testid="why-us-section">
      <div className="absolute inset-0 grain-bg opacity-20 pointer-events-none" aria-hidden />
      <div className="container-pad mx-auto max-w-7xl relative">
        <div className="max-w-3xl mb-14">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#2A9D8F] mb-5">
            Why families choose Julicare
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]" data-testid="why-us-heading">
            The difference is in the details, and the people.
          </h2>
          <p className="mt-6 text-[#F8FAFC]/80 text-base md:text-lg leading-relaxed max-w-2xl">
            We blend clinical excellence with the warmth of family. Every shift,
            every visit, every conversation is shaped by six commitments.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.05 }}
              className="rounded-3xl border border-[#F8FAFC]/10 bg-[#F8FAFC]/5 p-7 md:p-8 hover:bg-[#F8FAFC]/10 transition-colors"
              data-testid={`why-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="h-12 w-12 rounded-2xl bg-[#7FB3D5]/25 flex items-center justify-center mb-5">
                <f.icon size={22} className="text-[#F8FAFC]" />
              </div>
              <h3 className="font-serif text-xl md:text-2xl leading-tight">{f.title}</h3>
              <p className="mt-3 text-sm md:text-base text-[#F8FAFC]/75 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
